import { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { checkObjectHasEmptyProperty } from 'src/app/utils/checkObjectHasEmptyProperty';
import { log } from 'src/app/utils/logging';
import { getSupportedCategoryCountry } from 'src/app/views/components/JobCategory/utils/getCategoryCountry';
import { usePostedJobIdSelector } from 'src/app/views/hooks/usePostedJobIdSelector';
import { useGetSuggestedSalaryVariables } from 'src/app/views/hooks/useSuggestedSalary';
import {
  useTrackSuggestedCategoriesLazyQuery,
  useTrackSuggestedSalaryLazyQuery,
} from 'src/graphql/generated';
import type AppState from 'src/types/appState';

const TrackingForSuggestedSalary = () => {
  const suggestedSalaryQueryVariables = useGetSuggestedSalaryVariables();
  const [trackSuggestedSalaryLazyQuery] = useTrackSuggestedSalaryLazyQuery();

  const postedJobId = usePostedJobIdSelector();

  useEffect(() => {
    const variables = { ...suggestedSalaryQueryVariables, jobId: postedJobId };

    if (checkObjectHasEmptyProperty(variables)) {
      log.warn(
        'TrackingForSuggestedApi | trackSuggestedSalaryLazyQuery | missing data',
        variables,
      );

      return;
    }

    trackSuggestedSalaryLazyQuery({
      variables: {
        ...variables,
        currency: variables.currency!,
        jobId: variables.jobId!,
      },
      onError: (error) => {
        log.error(
          'TrackingForSuggestedApi | trackSuggestedSalaryLazyQuery | onError',
          {
            error,
            ...variables,
          },
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

const TrackingForSuggestedCategories = () => {
  const { jobTitle, jobLocationCountry } = useSelector(
    (state: AppState) => ({
      jobTitle: state.formData.jobTitle,
      jobLocationCountry: state.session.jobLocationCountry,
    }),
    shallowEqual,
  );

  const [trackSuggestedCategoriesLazyQuery] =
    useTrackSuggestedCategoriesLazyQuery();

  const postedJobId = usePostedJobIdSelector();

  useEffect(() => {
    const country = getSupportedCategoryCountry(jobLocationCountry);

    if (!country || !postedJobId || !jobTitle) {
      log.warn(
        'TrackingForSuggestedApi | trackSuggestedCategoriesLazyQuery | missing data',
        { country, jobId: postedJobId, jobTitle },
      );

      return;
    }

    trackSuggestedCategoriesLazyQuery({
      variables: {
        jobId: parseInt(postedJobId, 10),
        jobTitle,
        country,
      },
      onError: (error) => {
        log.error(
          'TrackingForSuggestedApi | trackSuggestedCategoriesLazyQuery | onError',
          { error, country, jobId: postedJobId, jobTitle },
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export const TrackingForSuggestedApi = () => (
  <>
    <TrackingForSuggestedSalary />
    <TrackingForSuggestedCategories />
  </>
);
